import React from 'react';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import CommentBoxUserName from './CommentBoxUserName';
import CommentEditor from './CommentEditor';
import CommentActionMenu from './CommentActionMenu';
import CommentInlineError from './CommentInlineError';
import CommentTimestamp from './CommentTimestamp';
import { CommentData } from '../types';
import CommentDeleted from './CommentDeleted';
import CommentBoxContent from './CommentBoxContent';

interface CommentBoxProps extends CommentData {
  /**
   * If error exists, renders the default error message
   */
  error?: string;
  onSave: (id: string, value: string) => Promise<void>;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
  onViewed: (id: string, markAsViewed: boolean) => void;
}

enum CommentBoxState {
  View,
  Edit,
}

const CommentUnviewedIndicator = () => {
  return (
    <div
      role="status"
      aria-label="Read indicator"
      style={{
        height: 6,
        width: 6,
        backgroundColor: palette.azure_300,
        borderRadius: '50%',
      }}
    />
  );
};

const CommentContent = ({
  commentState,
  text,
  editText,
  onChange,
  onSave,
  onCancel,
}: {
  commentState: CommentBoxState;
  text: string;
  editText: string;
  onChange: (value: string) => void;
  onSave: (value: string) => Promise<void>;
  onCancel: ({ resetEditText }: { resetEditText?: boolean }) => void;
}) => {
  return (
    <>
      {commentState === CommentBoxState.View && (
        <CommentBoxContent text={text} />
      )}
      {commentState === CommentBoxState.Edit && (
        <CommentEditor
          text={editText}
          onChange={onChange}
          onSubmit={onSave}
          onCancel={onCancel}
        />
      )}
    </>
  );
};

const CommentBox = (props: CommentBoxProps) => {
  const {
    id,
    index,
    userId,
    text,
    createdAt,
    deletedAt,
    commentOwner,
    authorUserId,
    viewed = false,
    error,
    onSave,
    onDelete,
    onCancel,
    onViewed,
  } = props;
  const { classes } = useStyles();
  const isSelf = commentOwner?.id === userId;
  const [commentState, setCommentState] = React.useState(CommentBoxState.View);
  const [editText, setEditText] = React.useState('');

  let selectButtonAriaLabel = commentOwner
    ? `${commentOwner?.name} comment`
    : 'Deactivated user comment';
  if (!viewed) {
    selectButtonAriaLabel += ', enter or space to mark as read';
  } else {
    selectButtonAriaLabel += ' read';
  }

  const handleEdit = React.useCallback(() => {
    if (commentState !== CommentBoxState.Edit) {
      setEditText(text);
      setCommentState(CommentBoxState.Edit);
    }
  }, [commentState, text]);

  const handleCancel = React.useCallback(
    ({ resetEditText }: { resetEditText?: boolean }) => {
      if (resetEditText) {
        // Reset to initial text
        setEditText(text);
      }
      setCommentState(CommentBoxState.View);
      onCancel(id);
    },
    [id, onCancel, text]
  );

  const handleSave = React.useCallback(
    (value: string) => {
      return onSave(id, value);
    },
    [id, onSave]
  );

  const handleClick = () => {
    if (!viewed) onViewed(id, true);
  };

  const handleDelete = React.useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  const handleViewed = React.useCallback(
    (markAsViewed: boolean) => {
      if (markAsViewed !== viewed) onViewed(id, markAsViewed);
    },
    [id, onViewed, viewed]
  );

  if (deletedAt) return <CommentDeleted id={id} index={index} />;

  return (
    <div
      id={`wsl-comment-${id}`}
      role="article"
      aria-posinset={index}
      className={classes.articleContainer}
    >
      <button
        type="button"
        className={classes.selectButton}
        data-viewed={viewed}
        onClick={handleClick}
        aria-label={selectButtonAriaLabel}
      >
        {''}
      </button>
      <div className={classes.container}>
        <div className={classes.header}>
          <CommentBoxUserName
            createdAt={createdAt}
            commentOwner={commentOwner}
            authorUserId={authorUserId}
          />
          <div>
            <CommentTimestamp createdAt={createdAt} />
          </div>
          <div>{!viewed && <CommentUnviewedIndicator />}</div>
          <div className={classes.headerMenu}>
            <CommentActionMenu
              id={id}
              isSelf={isSelf}
              viewed={viewed}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewed={handleViewed}
            />
          </div>
        </div>
        <CommentContent
          commentState={commentState}
          text={text}
          editText={editText}
          onCancel={handleCancel}
          onSave={handleSave}
          onChange={setEditText}
        />
        {error && <CommentInlineError error={error} />}
      </div>
    </div>
  );
};

const useStyles = makeStyles()(theme => ({
  articleContainer: {
    borderRadius: theme.spacing(0.5),
    backgroundColor: palette.gray_1000,
    position: 'relative',
    '&:hover': {
      backgroundColor: palette.gray_900,
      '& .wsl-comment-menu-trigger': {
        opacity: 1,
      },
    },
    '& .wsl-comment-menu-trigger': {
      opacity: 0,
      '&:focus-visible': {
        opacity: 1,
      },
    },
    '& .wsl-comment-menu-trigger[data-state="open"]': {
      opacity: 1,
    },
  },
  container: {
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 1.5, 2),
    color: palette.gray_0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  header: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  headerMenu: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(1),
  },
  selectButton: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    border: 'unset',
    borderRadius: theme.spacing(0.5),
    backgroundColor: 'transparent',
    padding: 0,
    '&:focus': {
      outline: `1px solid ${palette.gray_400}`,
    },
    '&:focus-visible': {
      outline: `1px solid ${palette.mint_200}`,
    },
    '&[data-viewed="true"]': {
      cursor: 'default',
    },
  },
}));

export default React.memo(CommentBox);
